import { isClonedVoiceCode } from '@vbee-holding/aivoice-shared-lib';
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _, { groupBy, keys } from 'lodash';
import {
  OUTPUT_AUDIO,
  LANGUAGE_CODE,
  TTS_ATTRIBUTE,
  VOICE_PROVIDER,
  NON_VBEE_VOICE_PROVIDERS,
} from '@src/constants/voice';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  EditorState,
  CompositeDecorator,
  Modifier,
  SelectionState,
} from 'draft-js';
import { OrderedSet } from 'immutable';
import {
  TextField,
  InputAdornment,
  IconButton,
  ClickAwayListener,
  Button,
} from '@mui/material';
import {
  SearchRounded,
  TextFieldsRounded,
  CloseRounded,
  CachedRounded,
  MoreVertRounded as MoreVertRoundedIcon,
} from '@mui/icons-material';
import apis from '@src/apis';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import actions from '@src/redux/actions';
import { FEATURE, PACKAGE_STUDIO_VERSION } from '@src/constants/package';
import { CLOSE_DICTIONARY_GUIDE_DESKTOP } from '@src/constants/dictionary';
import BackgroundMusic from '@src/containers/BackgroundMusic';
import {
  handleInsertElement,
  handleReplace,
  handleEditParagraphs,
} from '@src/services/paragraph';
import { getEntityStrategy } from '@src/services/entity';
import { generateDecorator, findWithRegexEditor } from '@src/services/editor';
import { handleInsertElementOfSentence } from '@src/services/sentence';
import { checkFeaturePermission } from '@src/services/tts';
import NewDictionary from '@src/containers/NewDictionary';
import HightLightSpan from '@src/components/Decorator/HightLightSpan';
import DictionaryGuide from '@src/containers/NewTourGuide/DictionaryGuide';
import { SUB_STATUS } from '@src/constants/voiceCloning';
import useUserInfo from '@src/hooks/useTtsUser';
import { COLOR } from '@src/styles/color';
import ProcessingMethods from '../ProcessingMethods';

import {
  StyledToolbar,
  StyleMoreButtonPopper,
  StyleContentPopper,
} from './index.style';
import ButtonProject from './ButtonProject';
import AdjustVoice from './AdjustVoice';
import ImportData from './ImportData';
import AdjustAudio from './AdjustAudio';
import BreakTime from './BreakTime';
import AdjustBackgroundMusic from './AdjustBackgroundMusic';
import SearchAndReplace from './SearchAndReplace';
import OpenDictionary from './OpenDictionary';
import OpenClientPause from './OpenClientPause';
import TTSPreset from './TTSPreset';
import VoicePresetDialog from '../VoicePresetDialog';
import ClientPauseNew from '../ClientPauseNew';

const ToolbarV3 = ({
  content,
  onOpenVoice,
  toolbarRef,
  openSentences,
  useNewTtsUI,
  handleChangeContent,
  onChangeInputLength,
  onChangeCharacterExceed,
  onOpenSentences,
  enableEditor,
  onEnableEditor,
  closeMoreMenu,
  setCloseMoreMenu,
  handleChangeVoice,
}) => {
  const { getFeatureValue } = useFeatureFlags();
  const toolbarRightRef = useRef(null);
  const [search, setSearch] = useState('');
  const [ttsPresetData, setTTSPresetData] = useState();
  const [loadingGetListTTSPreset, setLoadingGetListTTSPreset] = useState(true);
  const [replace, setReplace] = useState('');
  const [openBonusToolbar, setOpenBonusToolbar] = useState(false);
  const [openMusic, setOpenMusic] = useState(false);
  const [openDictionary, setOpenDictionary] = useState(false);
  const [openDictionaryGuide, setOpenDictionaryGuide] = useState(false);
  const [openClientPause, setOpenClientPause] = useState(false);
  const [openMoreButton, setOpenMoreButton] = useState(false);
  const [anchorElMoreButton, setAnchorElMoreButton] = useState(null);
  const [openSearchBar, setOpenSearchBar] = useState(false);
  const [showMargin, setShowMargin] = useState(false);
  const [openVoicePresetDialog, setOpenVoicePresetDialog] = useState(false);
  const [openTTSPreset, setOpenTTSPreset] = useState(false);
  const [ttsPresetDialogData, setTtsPresetDialogData] = useState(null);
  const initOffsetTop = toolbarRightRef?.current?.offsetTop;

  const [anchorElOpenBonusToolbar, setAnchorElOpenBonusToolbar] =
    useState(null);
  const {
    synthesisRequest,
    paragraphs,
    // selectedParagraph,
    selectedSentence,
    sentences,
    advanceFeature,
    selectedSentences,
    ttsPreset,
  } = useSelector((state) => state.synthesisRequest);
  const { usingPackage } = useSelector((state) => state.user);
  const { ttsUser } = useUserInfo();
  const { user } = useSelector((state) => state.auth);
  const { voice } = useSelector((state) => state.synthesisRequest);

  const isPublicVoiceCloning = voice?.status === SUB_STATUS.PUBLIC;
  const isVoiceCloningAboutToRemoved =
    isPublicVoiceCloning && voice?.discardAt
      ? moment().isBefore(voice?.discardAt)
      : false;

  const isInValidPublicVoiceCloning =
    voice?.userId !== user?.id &&
    voice?.discardAt &&
    moment().isAfter(voice?.discardAt);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentOpenMoreButton = useRef(false);
  const currentAnchorElMoreButton = useRef(null);
  const currentOpenBonusToolbar = useRef(false);
  const currentAnchorElBonusToolbar = useRef(null);

  const outputAudioTypes = OUTPUT_AUDIO.map((item) => item.type);

  const handleClickMoreButton = (event) => {
    setAnchorElMoreButton(openMoreButton ? null : event.currentTarget);
    setOpenMoreButton((previousOpen) => !previousOpen);
    setOpenBonusToolbar(false);
    setOpenSearchBar(false);
    setAnchorElOpenBonusToolbar(null);
    // setCloseMoreMenu((previousState) => !previousState);
    currentOpenMoreButton.current = !currentOpenMoreButton.current;
    currentAnchorElMoreButton.current = event.currentTarget;
  };

  const getListTTSPreset = async () => {
    const response = await apis.ttsPreset.getListTTSPreset();
    if (response.status) {
      setTTSPresetData(response.result);
    }
    setLoadingGetListTTSPreset(false);
  };

  useEffect(() => {
    getListTTSPreset();
  }, []);

  const handleClickAwayMoreButton = () => {
    if (!openSearchBar) {
      setAnchorElMoreButton(null);
      setOpenMoreButton(false);
    }
    setCloseMoreMenu(false);
  };

  const handleCloseSearchBoxAndMoreMenu = () => {
    setOpenBonusToolbar(false);
    setAnchorElMoreButton(null);
    setOpenMoreButton(false);
    setOpenSearchBar(false);
  };

  const handleCloseSearchBox = () => {
    setOpenBonusToolbar(false);
    setOpenSearchBar(false);
  };

  const handleClickOpenBonusToolbar = (event) => {
    setAnchorElOpenBonusToolbar(openBonusToolbar ? null : event.currentTarget);
    setOpenBonusToolbar((previousOpen) => !previousOpen);
    setOpenSearchBar((previousOpen) => !previousOpen);
    setCloseMoreMenu(false);
    currentOpenBonusToolbar.current = !currentOpenBonusToolbar.current;
    currentAnchorElBonusToolbar.current = event.currentTarget;
  };

  const SearchHighlight = ({ children }) => (
    <span className="search-highlight">{children}</span>
  );

  const handleChangeSearch = (e) => {
    e.persist();
    setSearch(e.target.value);

    if (openSentences) return;

    const regex = new RegExp(_.escapeRegExp(e.target.value), 'gi');
    const stateContent = EditorState.set(content, {
      decorator: generateDecorator(regex, SearchHighlight, e.target.value),
    });
    handleChangeContent(stateContent);
  };

  const updateRequestStatus = () => {
    if (!enableEditor) {
      onEnableEditor(true);
      dispatch(actions.synthesisRequest.cloneSynthesisRequest(true));
    }
  };

  const newHandleChangeSearch = (e) => {
    setSearch(e.target.value);
    if (e.target.value === '') {
      const stateContent = EditorState.set(content, {
        decorator: new CompositeDecorator([
          {
            strategy: getEntityStrategy('MUTABLE'),
            component: HightLightSpan,
          },
        ]),
      });
      handleChangeContent(stateContent);
      return;
    }
    const regex = new RegExp(_.escapeRegExp(e.target.value), 'gi');
    const stateContent = EditorState.set(content, {
      decorator: new CompositeDecorator([
        {
          strategy: getEntityStrategy('MUTABLE'),
          component: HightLightSpan,
        },
        {
          strategy: (contentBlock, callback) => {
            if (e.target.value !== '')
              findWithRegexEditor(regex, contentBlock, callback);
          },
          component: SearchHighlight,
        },
      ]),
    });
    handleChangeContent(stateContent);
  };

  const handleSearch = () => {
    if (openSentences)
      dispatch(actions.synthesisRequest.changeSearchContent(search));
    else {
      const regex = new RegExp(search, 'gi');
      const stateContent = EditorState.set(content, {
        decorator: new CompositeDecorator([
          {
            strategy: getEntityStrategy('MUTABLE'),
            component: HightLightSpan,
          },
          {
            strategy: (contentBlock, callback) => {
              if (search !== '')
                findWithRegexEditor(regex, contentBlock, callback);
            },
            component: SearchHighlight,
          },
        ]),
      });
      handleChangeContent(stateContent);
    }
  };

  const handleEnterKeySearch = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleChangeReplace = () => {
    if (!search || !replace) return;
    if (openSentences)
      dispatch(actions.synthesisRequest.replaceInSentences(replace));
    else {
      const escapedSearch = _.escapeRegExp(search);
      const regex = new RegExp(escapedSearch, 'gi');
      const selectionsToReplace = [];
      const blockMap = content.getCurrentContent().getBlockMap();
      const delta = replace.length - search.length;
      let blockIndex = 0;
      let currentBlockKey = '';
      blockMap.forEach((contentBlock) =>
        findWithRegexEditor(regex, contentBlock, (start, end) => {
          const blockKey = contentBlock.getKey();
          if (currentBlockKey !== blockKey) {
            blockIndex = 0;
            currentBlockKey = blockKey;
          }
          const blockSelection = SelectionState.createEmpty(blockKey).merge({
            anchorOffset: start + blockIndex * delta,
            focusOffset: end + blockIndex * delta,
          });
          blockIndex += 1;
          selectionsToReplace.push(blockSelection);
        }),
      );

      let contentState = content.getCurrentContent();

      selectionsToReplace.forEach((selectionState) => {
        contentState = Modifier.replaceText(
          contentState,
          selectionState,
          replace,
        );
      });
      handleChangeContent(EditorState.push(content, contentState));
      updateRequestStatus();
    }
  };

  const newHandleChangeReplace = () => {
    if (!search || !replace) return;

    const contentState = handleReplace({
      search,
      replace,
      editorState: content,
      paragraphs,
    });
    const newEditorState = EditorState.push(content, contentState);
    handleChangeContent(EditorState.push(content, contentState));

    const newParagraphs = handleEditParagraphs(paragraphs, newEditorState);
    if (newParagraphs !== null) {
      dispatch(actions.synthesisRequest.updateParagraphs(newParagraphs));
      dispatch(actions.synthesisRequest.updateLoadingParagraphs(true));
    }
  };

  const handleEnterKeyReplace = (event) => {
    if (event.key === 'Enter') handleChangeReplace();
  };

  const handleOpenMusic = () => {
    setOpenMusic(true);
    handleCloseSearchBoxAndMoreMenu();
  };
  const handleCloseMusic = () => setOpenMusic(false);

  const checkShouldShowDictionaryGuide = () => {
    const dateDiff = moment().diff(user?.createdAt, 'days');
    const closeDictionaryGuideBefore = localStorage.getItem(
      CLOSE_DICTIONARY_GUIDE_DESKTOP,
    );

    return dateDiff > 7 && !closeDictionaryGuideBefore;
  };

  const handleOpenDictionary = (showGuide) => {
    setOpenDictionaryGuide(false);
    if (showGuide) localStorage.setItem(CLOSE_DICTIONARY_GUIDE_DESKTOP, true);

    setOpenDictionary(true);
    handleCloseSearchBoxAndMoreMenu();
  };
  const handleCloseDictionary = () => setOpenDictionary(false);
  const handleChangeOpenDictionaryGuide = (value) =>
    setOpenDictionaryGuide(value);

  const handleClickButtonDictionary = (isInMoreButton) => {
    const hasFeature = checkFeaturePermission(
      ttsUser?.features,
      FEATURE.CUSTOMIZATION_PRONUNCIATION,
    );
    const isPackageV2 = usingPackage?.version === PACKAGE_STUDIO_VERSION.V2;

    if (!hasFeature && isPackageV2) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('limitedFeatureDesc', {
            feature: t('customization-pronunciation'),
          }),
        }),
      );
      return;
    }

    const shouldShowGuide = checkShouldShowDictionaryGuide();
    if (!isInMoreButton && shouldShowGuide) {
      handleChangeOpenDictionaryGuide(true);
      return;
    }
    handleOpenDictionary(shouldShowGuide);
  };

  const hasClientPause =
    usingPackage.id &&
    checkFeaturePermission(ttsUser.features, FEATURE.CLIENT_PAUSE) &&
    voice?.language?.code === LANGUAGE_CODE.VIETNAMESE;
  const handleOpenClientPause = () => {
    if (!hasClientPause) {
      dispatch(actions.dialog.displayDialog({ limitedFeature: true }));
      return;
    }
    setOpenClientPause(true);
    handleCloseSearchBoxAndMoreMenu();
  };

  const handleCloseClientPause = () => setOpenClientPause(false);

  const handleAudioTypeFeatureCheck = (name, value) => {
    const isPackageV2 = usingPackage?.version === PACKAGE_STUDIO_VERSION.V2;
    if (name === 'audioType' && isPackageV2) {
      const ouputAudio = OUTPUT_AUDIO.find((item) => item.type === value);
      const hasFeature = checkFeaturePermission(
        ttsUser?.features,
        ouputAudio?.feature,
      );
      if (!hasFeature) {
        dispatch(
          actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
            description: t('limitedFeatureDesc', {
              feature: t(ouputAudio.featureDesc),
            }),
          }),
        );
        return false;
      }
    }
    return true;
  };

  const handleChangeSynthesisRequest = (name, value) => {
    if (!handleAudioTypeFeatureCheck(name, value)) return;

    const keyNotValid = openSentences && ['speed', 'breakTime']?.includes(name);
    const shouldUpdateRequest = synthesisRequest[name] !== value;
    if (shouldUpdateRequest) {
      const keyPreset = ['audioType', 'speed'];
      if (keyPreset.includes(name) && ttsPreset) {
        dispatch(actions.synthesisRequest.updateCurrentTTSPreset(null));
      }
      dispatch(actions.synthesisRequest.updateSynthesisRequest(name, value));

      if (!keyNotValid) {
        updateRequestStatus();
      }
    }
  };

  const handleChangeBreakTime = (value) => {
    handleChangeSynthesisRequest('breakTime', value);
    if (openSentences) return;
    const currentContent = content.getCurrentContent();
    const selection = content.getSelection();
    const label = `<break time=${value}s/>`;
    const textWithEntity = Modifier.insertText(
      currentContent,
      selection,
      label,
      OrderedSet.of('BOLD'),
    );

    handleChangeContent(
      EditorState.push(content, textWithEntity, 'insert-characters'),
    );
  };

  const handleBreakTimeOfSentence = (value) => {
    const { sentenceId, focusPosition } = selectedSentence;
    if (sentenceId && focusPosition !== undefined) {
      const sentence = sentences.find((item) => item.id === sentenceId);
      if (!sentence) return;

      const newSentence = handleInsertElementOfSentence({
        elements: sentence.elements,
        sentenceText: sentence.text,
        focusOffset: selectedSentence.focusPosition,
        elementName: TTS_ATTRIBUTE.BREAK_TIME,
        elementValue: value,
      });

      const { elements, text } = newSentence;
      dispatch(actions.synthesisRequest.updateSelectedSentence({}));
      dispatch(
        actions.synthesisRequest.updateElementOfSentence({
          elements,
          text,
          sentenceId,
          onLoad: true,
          mousePointerPosition: focusPosition + `${value}s`.length,
        }),
      );
    }
  };

  const newHandleChangeBreakTime = (value) => {
    handleChangeSynthesisRequest('breakTime', value);
    if (advanceFeature && openSentences) {
      handleBreakTimeOfSentence(value);
    } else {
      const newParagraphs = handleInsertElement({
        paragraphs,
        editorState: content,
        elementName: TTS_ATTRIBUTE.BREAK_TIME,
        elementValue: value,
      });

      if (newParagraphs !== null) {
        dispatch(actions.synthesisRequest.updateParagraphs(newParagraphs));
        dispatch(actions.synthesisRequest.updateLoadingParagraphs(true));
      }
    }
  };

  const handleChangeBackgroundMusic = (name, link, volume) => {
    if (
      synthesisRequest?.backgroundMusic?.link !== link ||
      synthesisRequest?.backgroundMusic?.volume !== volume
    ) {
      if (ttsPreset) {
        dispatch(actions.synthesisRequest.updateCurrentTTSPreset(null));
      }
      dispatch(actions.synthesisRequest.updateVolumeBackgroundMusic(volume));
      dispatch(actions.synthesisRequest.updateBackgroundMusic(name, link));
      updateRequestStatus();
    }
  };

  const handleChangeSpeed = (name, value) => {
    handleChangeSynthesisRequest(name, value);
  };

  const handleChangeEmphasis = (name, value) => {
    handleChangeSynthesisRequest(name, value);
  };

  const handleOpenVoices = () => {
    onOpenVoice(true);
    handleCloseSearchBoxAndMoreMenu();
  };

  const handleCloseVoicePresetDialog = () => {
    setOpenVoicePresetDialog(false);
    setTtsPresetDialogData(null);
  };
  const handleOpenVoicePresetDialog = (data) => {
    setTtsPresetDialogData(data);
    setOpenVoicePresetDialog(true);
  };

  const useNonVbeeBreakTime = getFeatureValue(
    FEATURE_KEYS.NON_VBEE_BREAK_TIME,
    {
      voiceProvider: voice?.provider,
      userId: user.id,
    },
  );

  const isDisabledWhenOffSentences = useMemo(() => {
    const isVoiceVbee = voice?.provider === VOICE_PROVIDER.VBEE;
    if (!useNonVbeeBreakTime || isVoiceVbee) {
      return (
        !advanceFeature &&
        !isClonedVoiceCode(voice?.code) &&
        voice?.provider !== VOICE_PROVIDER.VBEE
      );
    }
    return false;
  }, [advanceFeature, useNonVbeeBreakTime, voice]);

  const isDisabledWhenOpenSentences = useMemo(() => {
    const FIELD_GROUP = 'provider';
    const getSentencesSelected = sentences?.flatMap((item) =>
      selectedSentences?.includes(item?.id) ? item : [],
    );

    const groupProvider = groupBy(
      getSentencesSelected?.map((item) => item?.voice),
      FIELD_GROUP,
    );
    const isNonVbeeBreakTime = (provider) =>
      getFeatureValue(FEATURE_KEYS.NON_VBEE_BREAK_TIME, {
        voiceProvider: provider,
        userId: user.id,
      });

    const getKeysNonVbee = keys(groupProvider)?.flatMap((k) =>
      NON_VBEE_VOICE_PROVIDERS?.includes(k) ? k : [],
    );
    return Boolean(
      !getKeysNonVbee?.every((provider) => isNonVbeeBreakTime(provider)),
    );
  }, [sentences, selectedSentences, user]);

  const renderMoreButton = () => {
    const canBeOpen = currentOpenMoreButton.current;
    const id = canBeOpen ? 'transition-popper' : undefined;
    return (
      <>
        <div className="more-button">
          <IconButton
            className={`custom-button ${openMoreButton && 'active-button'}`}
            color="iconPrimary"
            onClick={handleClickMoreButton}
            aria-describedby={id}
          >
            <MoreVertRoundedIcon sx={{ fontSize: 24, color: COLOR.navyBlue }} />
          </IconButton>
        </div>
        {anchorElMoreButton && (
          <ClickAwayListener onClickAway={() => handleClickAwayMoreButton()}>
            <StyleMoreButtonPopper
              id={id}
              open={openMoreButton}
              anchorEl={anchorElMoreButton}
              placement="bottom"
              modifiers={[
                {
                  name: 'preventOverflow',
                  options: {
                    padding: { right: 40, left: 40 },
                  },
                },
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                },
              ]}
            >
              <StyleContentPopper>
                <OpenClientPause
                  isMoreButton
                  handleOpenClientPause={handleOpenClientPause}
                  hasClientPause={hasClientPause}
                />
                <AdjustAudio
                  isMoreButton
                  synthesisRequest={synthesisRequest}
                  outputAudioTypes={outputAudioTypes}
                  handleChangeSynthesisRequest={handleChangeSynthesisRequest}
                  handleCloseSearchBoxAndMoreMenu={
                    handleCloseSearchBoxAndMoreMenu
                  }
                  handleCloseSearchBox={handleCloseSearchBox}
                />
                <AdjustBackgroundMusic
                  isMoreButton
                  synthesisRequest={synthesisRequest}
                  handleChangeSynthesisRequest={handleChangeSynthesisRequest}
                  handleCloseSearchBoxAndMoreMenu={
                    handleCloseSearchBoxAndMoreMenu
                  }
                  handleCloseSearchBox={handleCloseSearchBox}
                  handleOpenMusic={handleOpenMusic}
                />
              </StyleContentPopper>
            </StyleMoreButtonPopper>
          </ClickAwayListener>
        )}
      </>
    );
  };

  const handleOpenTTSPreset = () => {
    setOpenTTSPreset(true);
  };

  const handleCloseTTSPreset = () => {
    setOpenTTSPreset(false);
  };

  const renderResponsiveToolbar = () => (
    <div className="left-toolbar">
      <AdjustVoice
        advanceFeature={advanceFeature}
        openSentences={openSentences}
        handleOpenVoices={handleOpenVoices}
        voice={voice}
        isInValidPublicVoiceCloning={isInValidPublicVoiceCloning}
        isVoiceCloningAboutToRemoved={isVoiceCloningAboutToRemoved}
        synthesisRequest={synthesisRequest}
        handleCloseSearchBoxAndMoreMenu={handleCloseSearchBoxAndMoreMenu}
        handleChangeSpeed={handleChangeSpeed}
        handleChangeSynthesisRequest={handleChangeSynthesisRequest}
        handleChangeEmphasis={handleChangeEmphasis}
      />
      <AdjustAudio
        synthesisRequest={synthesisRequest}
        outputAudioTypes={outputAudioTypes}
        handleChangeSynthesisRequest={handleChangeSynthesisRequest}
        handleCloseSearchBoxAndMoreMenu={handleCloseSearchBoxAndMoreMenu}
        handleCloseSearchBox={handleCloseSearchBox}
      />
      <OpenClientPause
        handleOpenClientPause={handleOpenClientPause}
        hasClientPause={hasClientPause}
      />
      <AdjustBackgroundMusic
        synthesisRequest={synthesisRequest}
        handleChangeSynthesisRequest={handleChangeSynthesisRequest}
        handleCloseSearchBoxAndMoreMenu={handleCloseSearchBoxAndMoreMenu}
        handleCloseSearchBox={handleCloseSearchBox}
        handleOpenMusic={handleOpenMusic}
      />
      {renderMoreButton()}
      {!openSentences && (
        <TTSPreset
          loadingGetListTTSPreset={loadingGetListTTSPreset}
          ttsPresetData={ttsPresetData}
          refetchListTTSPreset={getListTTSPreset}
          handleOpenVoicePresetDialog={handleOpenVoicePresetDialog}
          openVoicePresetDialog={openVoicePresetDialog}
          handleChangeVoice={handleChangeVoice}
          handleOpenTTSPreset={handleOpenTTSPreset}
          openTTSPreset={openTTSPreset}
          handleCloseTTSPreset={handleCloseTTSPreset}
        />
      )}
    </div>
  );

  const renderToolbarRight = () => (
    <div
      ref={toolbarRightRef}
      className={`toolbar-right ${showMargin ? 'margin-responsive' : ''}`}
    >
      <BreakTime
        synthesisRequest={synthesisRequest}
        openSentences={openSentences}
        isDisabledWhenOpenSentences={isDisabledWhenOpenSentences}
        isDisabledWhenOffSentences={isDisabledWhenOffSentences}
        advanceFeature={advanceFeature}
        newHandleChangeBreakTime={newHandleChangeBreakTime}
        handleChangeBreakTime={handleChangeBreakTime}
        handleCloseSearchBoxAndMoreMenu={handleCloseSearchBoxAndMoreMenu}
        handleCloseSearchBox={handleCloseSearchBox}
        handleOpenClientPause={handleOpenClientPause}
        hasClientPause={hasClientPause}
      />
      <OpenDictionary
        handleClickButtonDictionary={handleClickButtonDictionary}
      />
      <ImportData
        useNewTtsUI={useNewTtsUI}
        openSentences={openSentences}
        handleChangeContent={handleChangeContent}
        onChangeInputLength={onChangeInputLength}
        onChangeCharacterExceed={onChangeCharacterExceed}
        handleCloseSearchBoxAndMoreMenu={handleCloseSearchBoxAndMoreMenu}
        handleCloseSearchBox={handleCloseSearchBox}
      />
      <SearchAndReplace
        handleClickOpenBonusToolbar={handleClickOpenBonusToolbar}
      />
      <ProcessingMethods
        openSentences={openSentences}
        onOpenSentences={onOpenSentences}
        onEnableEditor={onEnableEditor}
        useToolbarV3
      />
      <ButtonProject />
    </div>
  );

  const handleResize = useCallback(() => {
    const MAX_RESPONSIVE_WIDTH = 1440;
    if (
      window.innerWidth > MAX_RESPONSIVE_WIDTH &&
      currentOpenMoreButton.current
    ) {
      currentOpenMoreButton.current = false;
      setOpenMoreButton(false);
    } else handleCloseSearchBoxAndMoreMenu();

    const { offsetTop } = toolbarRightRef?.current;

    if (offsetTop > initOffsetTop) {
      setShowMargin(true);
    } else {
      setShowMargin(false);
    }
  }, [initOffsetTop, showMargin]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [initOffsetTop, showMargin]);

  useEffect(() => {
    if (ttsPreset?.id !== undefined) {
      handleResize();
    }
  }, [ttsPreset?.id]);

  useEffect(() => {
    if (closeMoreMenu === true) {
      handleCloseSearchBoxAndMoreMenu();
      handleClickAwayMoreButton();
      setCloseMoreMenu(false);
    }
  }, [closeMoreMenu]);
  const handleCreateVoicePresetSuccess = (params) => {
    getListTTSPreset();
    if (params.isApply || ttsPreset?.id === params?.id) {
      dispatch(
        actions.synthesisRequest.updateSynthesisConfig({
          ...synthesisRequest,
          ...(params.audioType && { audioType: params.audioType }),
          ...(params.backgroundMusic && {
            backgroundMusic: params.backgroundMusic,
          }),
          ...(params.speed && { speed: params.speed }),
          ...(params.volume && { volume: params.volume }),
          ...(params.clientPause && {
            clientPause: params.clientPause,
          }),
          ...(params.voice && { voice: params.voice }),
        }),
      );
      dispatch(actions.synthesisRequest.updateCurrentTTSPreset(params));
      handleChangeVoice(params.voice);
    }
    if (params.isApply) {
      setOpenTTSPreset(false);
    }
  };

  return (
    <StyledToolbar
      ref={toolbarRef}
      openbonus={openBonusToolbar}
      allowresponsive
    >
      <div className="toolbar-row">
        {renderResponsiveToolbar()}
        {renderToolbarRight()}
      </div>
      <StyleMoreButtonPopper
        id="search-and-replace"
        open={openBonusToolbar}
        anchorEl={anchorElOpenBonusToolbar}
        placement="bottom"
        modifiers={[
          {
            name: 'preventOverflow',
            options: {
              padding: { right: 40, left: 40 },
            },
          },
          {
            name: 'offset',
            options: {
              offset: [0, 12],
            },
          },
        ]}
      >
        <TextField
          variant="outlined"
          className="search-input margin-right-bonus-toolbar"
          placeholder={t('search')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="end">
                <SearchRounded
                  onClick={handleSearch}
                  className="icon"
                  sx={{ fontSize: 20 }}
                />
              </InputAdornment>
            ),
            inputProps: {
              'data-id': 'input-text-search',
            },
          }}
          value={search}
          onChange={advanceFeature ? newHandleChangeSearch : handleChangeSearch}
          onKeyPress={handleEnterKeySearch}
        />
        <TextField
          variant="outlined"
          className="search-input margin-right-bonus-toolbar"
          placeholder={t('replace')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="end">
                <TextFieldsRounded
                  className="icon"
                  onClick={
                    advanceFeature
                      ? newHandleChangeReplace
                      : handleChangeReplace
                  }
                  sx={{ fontSize: 20 }}
                />
              </InputAdornment>
            ),
            inputProps: {
              'data-id': 'input-text-replace',
            },
          }}
          value={replace}
          onChange={(e) => setReplace(e.target.value)}
          onKeyPress={handleEnterKeyReplace}
        />
        <Button
          variant="outlined"
          onClick={handleChangeReplace}
          color="secondary"
          startIcon={<CachedRounded />}
          className="replace-button"
          data-id="button-replace"
          disabled={!replace || !search}
        >
          {t('replace')}
        </Button>
        <IconButton
          color="iconPrimary"
          className="close-button"
          onClick={() => handleCloseSearchBoxAndMoreMenu()}
          data-id="close-search-replace"
        >
          <CloseRounded className="icon" sx={{ fontSize: 20 }} />
        </IconButton>
      </StyleMoreButtonPopper>

      <BackgroundMusic
        open={openMusic}
        currentBackgroundMusic={synthesisRequest.backgroundMusic}
        name="backgroundMusic"
        onClose={handleCloseMusic}
        onChange={handleChangeBackgroundMusic}
        showUiChangeVolume
        volume={synthesisRequest.backgroundMusic?.volume || 0}
        max={100}
      />
      {openDictionary && (
        <NewDictionary open={openDictionary} onClose={handleCloseDictionary} />
      )}

      {openDictionaryGuide && (
        <DictionaryGuide
          run={openDictionaryGuide}
          onChange={handleChangeOpenDictionaryGuide}
          stepDisplay={false}
          onViewDetail={handleOpenDictionary}
        />
      )}
      <ClientPauseNew
        toolbarV3
        open={openClientPause}
        onClose={handleCloseClientPause}
      />
      <VoicePresetDialog
        open={openVoicePresetDialog}
        onClose={handleCloseVoicePresetDialog}
        onCreateSuccess={handleCreateVoicePresetSuccess}
        ttsPresetDialogData={ttsPresetDialogData}
        initValues={{ ...synthesisRequest, voice }}
      />
    </StyledToolbar>
  );
};

export default ToolbarV3;
